import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import flex from '../css/module/flex.css';
import style from '../css/module/chart-single-value.css';
import { cellFormatter } from '../util/helpers/chart-formatting-helpers';
import {
  CONFIG_TARGET,
  CONFIG_GAUGE,
  CONFIG_TARGET_VALUE,
  CONFIG_TARGET_COLOR,
  CONFIG_FONT_SIZE,
  DEFAULT_PROGRESS_COLOR,
  CONFIG_SHOW_GROWTH,
  CONFIG_ROW_INDEX,
  CONFIG_DISPLAY_AS,
  CONFIG_HIDE_DATE,
} from './charting/chartingConfiguration.constants';
import { INT, FLOAT } from '../util/ColumnType';
import { CHART_SINGLE_VALUE } from '../constants/test-ids';

export const PERCENTAGE = 'percentage';
export const RAW_VALUE = 'raw value';
export const BAR_DISPLAY = 'bar';
export const GUAGE_DISPLAY = 'guage';

class ChartSingleValue extends Component {
  target(value, formatted, width, backgroundColor, displayProgress) {
    const barStyle = {
      width: `${width}%`,
      backgroundColor,
      transition: 'width 0.2s',
    };

    return (
      <div className={style.progressBarContainer}>
        <div className={style.progressBarOuter}>
          <div className={style.progressBarFill} style={barStyle} />
        </div>
        {displayProgress}
      </div>
    );
  }

  gauge(backgroundColor, width) {
    const { isDashboard } = this.props;

    const gaugeStyle = {
      transform: `rotate(${width / 2 / 100}turn)`,
      background: backgroundColor,
    };

    const coverStyle = isDashboard ? { background: '#fff' } : {};

    return (
      <div className={style.gauge}>
        <div className={style.gaugeBody}>
          <div className={style.gaugeFill} style={gaugeStyle} />
          <div className={style.gaugeCover} style={coverStyle} />
        </div>
      </div>
    );
  }

  growthLabel(value, prevValue) {
    const { getChartOption } = this.props;

    const displayAs = getChartOption(CONFIG_DISPLAY_AS);

    const growth =
      displayAs === RAW_VALUE
        ? value - prevValue
        : (value / prevValue - 1) * 100;

    const rounded = Math.round(growth);

    const result =
      displayAs === RAW_VALUE ? (
        rounded
      ) : prevValue === 0 && value > 0 ? (
        <FontAwesomeIcon icon="infinity" />
      ) : (
        `${rounded}%`
      );

    return (
      <div className={flex.alignItemsCenter}>
        <div
          className={`${style.arrow} ${growth >= 0 ? style.up : style.down}`}
        >
          <FontAwesomeIcon icon={growth >= 0 ? 'arrow-up' : 'arrow-down'} />
        </div>
        <div
          className={`${style.selectedDate} ${
            growth >= 0 ? style.up : style.down
          }`}
        >
          {result}
        </div>
      </div>
    );
  }

  render() {
    // dataTable is either a single value (i.e. one cell) OR a 2 column table with a date and an INT (with the INT from the first row shown by default)
    const { dataTable, getChartOption, isSingleCell, isSingleValueWithTrend } =
      this.props;

    if (dataTable.rows().length === 0) {
      return (
        <div className={style.container}>
          <small className="faint">0 results</small>
        </div>
      );
    }

    const headings = dataTable.headings();

    // config options
    const showTarget = getChartOption(CONFIG_TARGET);
    const target = getChartOption(CONFIG_TARGET_VALUE);
    const fontSize = getChartOption(CONFIG_FONT_SIZE);
    const backgroundColor =
      getChartOption(CONFIG_TARGET_COLOR) || DEFAULT_PROGRESS_COLOR;
    const displayAsGuage = getChartOption(CONFIG_GAUGE) !== BAR_DISPLAY; // display as guage by default

    // config options specific isSingleValueWithTrend
    const rowIndex = getChartOption(CONFIG_ROW_INDEX);

    const isSingleColumnWithMultipleRows =
      headings.length === 1 && dataTable.rows().length > 1;

    // the default row to extract the cell from
    const rowIndexToUse =
      isSingleCell || !rowIndex || isSingleColumnWithMultipleRows
        ? 0
        : parseInt(rowIndex, 10) - 1; // substract one because, for user, the first row is row 1 (whereas the index for that row is 0);

    // if the row doesn't exist, show a "no data" message
    if (isSingleValueWithTrend && rowIndex > dataTable.rows().length) {
      return (
        <div className={style.container}>
          <span
            className={`faint ${flex.column} ${flex.alignItemsCenter}`}
            style={{ fontSize: '18px' }}
          >
            <span>No data</span>
            <small
              className="margin-top-10"
              style={{ fontWeight: '500', fontSize: '0.77em' }}
            >
              (Selected row doesn't exist)
            </small>
          </span>
        </div>
      );
    }

    // the position in the cells array of the value to display.
    const cellIndex =
      isSingleCell || isSingleColumnWithMultipleRows
        ? 0 // first col for single values
        : 1; // second col for tables

    // the raw value to be displayed (needs to be formatted)
    const value = dataTable.rows()[rowIndexToUse].cells()[cellIndex].value();
    const formatted = cellFormatter(dataTable, getChartOption, cellIndex, true);
    const cellValue = formatted(value);

    // used when isSingleValueWithTrend
    const dateFormatter = cellFormatter(dataTable, getChartOption, 0, true);

    const valueStyle = { fontSize: fontSize ? `${fontSize}px` : '35px' };

    let valueToDisplay = (
      <div
        style={valueStyle}
        className={`${style.valueContainer} text-align-center`}
        data-testid={CHART_SINGLE_VALUE}
      >
        {cellValue}
      </div>
    );

    // if col type is interval, can't show progress bar or anything else.
    if (
      headings.length === 1 &&
      ![INT, FLOAT].includes(headings[0].columnType().type())
    ) {
      return <div className={style.container}>{valueToDisplay}</div>;
    }

    if (isSingleValueWithTrend) {
      const hideDate = getChartOption(CONFIG_HIDE_DATE);
      const showGrowth = getChartOption(CONFIG_SHOW_GROWTH);
      const showGrowthLabel = showGrowth === undefined ? true : showGrowth;

      const mainDate = dataTable.rows()[rowIndexToUse].cells()[0].value();
      const previousDate =
        rowIndexToUse + 1 >= dataTable.rows().length
          ? null // data not in table
          : dataTable.rows()[rowIndexToUse + 1].cells()[0].value(); // data exists, so use that
      const previousValue =
        rowIndexToUse + 1 >= dataTable.rows().length
          ? null
          : dataTable.rows()[rowIndexToUse + 1].cells()[1].value();

      valueToDisplay = (
        <div
          meta-id="singleValueChart"
          className={`${hideDate ? '' : flex.column} ${flex.alignItemsCenter}`}
          style={valueStyle}
          data-testid={CHART_SINGLE_VALUE}
        >
          {hideDate ? null : (
            <small className={style.selectedDate}>
              {dateFormatter(mainDate)}
            </small>
          )}
          <div className={`${style.valueContainer} text-align-center w-100`}>
            {cellValue}
          </div>
          {showGrowthLabel && previousValue ? (
            <div className={flex.alignItemsCenter}>
              <div>{this.growthLabel(value, previousValue)}</div>
              {hideDate ? null : (
                <small className={style.selectedDate}>
                  &nbsp;from {dateFormatter(previousDate)}
                </small>
              )}
            </div>
          ) : null}
        </div>
      );
    }

    // progress bar
    const progress = !target ? 0 : (value / target) * 100;

    const width =
      !target || target === 0 ? 0 : progress <= 100 ? progress : 100;

    const displayProgress = (
      <div className={style.progressPercentage}>
        <span>{target === 0 ? 0 : progress.toFixed(0)}%</span>
        <span>&nbsp;{target ? `of ${formatted(target)}` : `of ${0}`}</span>
      </div>
    );

    return (
      <div className={style.container}>
        {showTarget && displayAsGuage ? (
          <div className={style.guageValueContainer}>
            {valueToDisplay}
            {displayProgress}
          </div>
        ) : (
          valueToDisplay
        )}

        {showTarget
          ? displayAsGuage
            ? this.gauge(backgroundColor, width)
            : this.target(
                value,
                formatted,
                width,
                backgroundColor,
                displayProgress,
              )
          : null}
      </div>
    );
  }
}

export default ChartSingleValue;
